import type { LegalDocument } from '@zeus/index'
import { useMutation, useQuery } from '@apollo/client/react'
import { GQL_ACCEPT_LEGAL_DOCUMENTS, GQL_MY_LEGAL_ACCEPTANCE } from '~/gql/consent/legal'
import { useAuth } from '~/helpers/store/auth'

/**
 * Documents légaux en attente d'acceptation pour l'utilisateur connecté (#360).
 * Partagé entre la fenêtre qui les fait accepter et `Root`, qui retient les autres
 * fenêtres tant qu'ils ne le sont pas : le cache Apollo ne fait qu'une requête.
 */
export function useLegalAcceptance() {
  const { payload } = useAuth()
  const isAuthenticated = Boolean(payload)

  const { data, loading, refetch } = useQuery(GQL_MY_LEGAL_ACCEPTANCE, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  })
  const [acceptMutation, { loading: accepting }] = useMutation(GQL_ACCEPT_LEGAL_DOCUMENTS, {
    // La réponse porte ce qui reste à accepter : on la reverse dans le cache de la query.
    update(cache, { data: accepted }) {
      if (accepted) cache.writeQuery({ query: GQL_MY_LEGAL_ACCEPTANCE, data: { myLegalAcceptance: accepted.acceptLegalDocuments } })
    },
  })

  const pending = isAuthenticated ? data?.myLegalAcceptance.pending ?? [] : []
  // Tant que la réponse n'est pas là, « rien en attente » n'est pas encore une
  // certitude : `Root` attend ce signal avant d'ouvrir la notice de suivi.
  const settled = !isAuthenticated || data !== undefined
  const accept = (documents: LegalDocument[]) => acceptMutation({ variables: { documents } })

  return { isAuthenticated, pending, settled, loading, accept, accepting, refetch }
}
